import api from "./api";

export type TableStatus = "AVAILABLE" | "OCCUPIED" | "RESERVED" | "BLOCKED";

export interface RestaurantTable {
  id: string;
  branchId?: string | null;
  salesPointId?: string | null;
  name: string;
  capacity: number;
  section: string | null;
  status: TableStatus;
  currentSaleId: string | null;
  isActive: boolean;
  currentSale?: {
    id: string;
    saleNumber: string;
    orderType: string;
    status: string;
    salesPointId?: string | null;
    createdAt: string;
  } | null;
}

export interface TableListResponse {
  tables: RestaurantTable[];
  grouped: Record<string, RestaurantTable[]>;
  total: number;
}

export interface TableSummaryResponse {
  summary: {
    total: number;
    available: number;
    occupied: number;
    reserved: number;
    blocked: number;
  };
  tables: RestaurantTable[];
}

const normalizeTable = (raw: Record<string, unknown>): RestaurantTable => {
  const currentSaleRaw = (raw.currentSale ?? raw.current_sale) as
    | Record<string, unknown>
    | null
    | undefined;

  return {
    id: String(raw.id ?? ""),
    branchId: raw.branchId ? String(raw.branchId) : raw.branch_id ? String(raw.branch_id) : undefined,
    salesPointId:
      (raw.salesPointId as string | null) ??
      (raw.sales_point_id as string | null) ??
      (raw.departmentId as string | null) ??
      (raw.department_id as string | null) ??
      null,
    name: String(raw.name ?? ""),
    capacity: Number(raw.capacity ?? 4),
    section: (raw.section as string | null) ?? null,
    status: (raw.status as TableStatus) ?? "AVAILABLE",
    currentSaleId:
      (raw.currentSaleId as string | null) ??
      (raw.current_sale_id as string | null) ??
      null,
    isActive: raw.isActive !== false && raw.is_active !== false,
    currentSale: currentSaleRaw
      ? {
          id: String(currentSaleRaw.id ?? ""),
          saleNumber: String(currentSaleRaw.saleNumber ?? currentSaleRaw.sale_number ?? ""),
          orderType: String(currentSaleRaw.orderType ?? currentSaleRaw.order_type ?? ""),
          status: String(currentSaleRaw.status ?? ""),
          salesPointId:
            (currentSaleRaw.salesPointId as string | null) ??
            (currentSaleRaw.sales_point_id as string | null) ??
            null,
          createdAt: String(currentSaleRaw.createdAt ?? currentSaleRaw.created_at ?? ""),
        }
      : null,
  };
};

const parseTableListPayload = (payload: unknown): TableListResponse => {
  const root = (payload ?? {}) as Record<string, unknown>;

  const rawTables = Array.isArray(root.tables)
    ? root.tables
    : Array.isArray(payload)
      ? payload
      : [];

  const tables = rawTables
    .map((row) => normalizeTable(row as Record<string, unknown>))
    .filter((t) => t.id && t.isActive);

  const groupedRaw = (root.grouped ?? {}) as Record<string, unknown[]>;
  const grouped: Record<string, RestaurantTable[]> = {};

  Object.entries(groupedRaw).forEach(([section, rows]) => {
    if (!Array.isArray(rows)) return;
    grouped[section] = rows
      .map((row) => normalizeTable(row as Record<string, unknown>))
      .filter((t) => t.id && t.isActive);
  });

  if (Object.keys(grouped).length === 0 && tables.length > 0) {
    tables.forEach((t) => {
      const key = t.section?.trim() || "General";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(t);
    });
  }

  return {
    tables,
    grouped,
    total: tables.length,
  };
};

export const tableAPI = {
  /** GET /tables?branchId=&salesPointId=&status=&section= */
  getTables: async (params: {
    branchId?: string;
    salesPointId?: string;
    status?: TableStatus;
    section?: string;
    sortBy?: "name" | "section" | "capacity" | "status";
    sortOrder?: "ASC" | "DESC";
  }): Promise<TableListResponse> => {
    const response = await api.get("/tables", { params });
    return parseTableListPayload(response.data?.data ?? response.data);
  },

  /** GET /tables/summary?branchId=&salesPointId= */
  getTableSummary: async (
    branchId?: string,
    salesPointId?: string
  ): Promise<TableSummaryResponse> => {
    const params: Record<string, string> = {};
    if (branchId) params.branchId = branchId;
    if (salesPointId) params.salesPointId = salesPointId;
    const response = await api.get("/tables/summary", { params });
    const payload = (response.data?.data ?? response.data ?? {}) as Record<
      string,
      unknown
    >;
    const summary = (payload.summary ?? {}) as Record<string, number>;
    const rawTables = Array.isArray(payload.tables) ? payload.tables : [];

    return {
      summary: {
        total: Number(summary.total ?? 0),
        available: Number(summary.available ?? 0),
        occupied: Number(summary.occupied ?? 0),
        reserved: Number(summary.reserved ?? 0),
        blocked: Number(summary.blocked ?? 0),
      },
      tables: rawTables
        .map((row) => normalizeTable(row as Record<string, unknown>))
        .filter((t) => t.id && t.isActive),
    };
  },

  getTableById: async (id: string): Promise<RestaurantTable> => {
    const response = await api.get(`/tables/${id}`);
    return normalizeTable(
      (response.data?.data ?? response.data ?? {}) as Record<string, unknown>
    );
  },

  clearTable: async (id: string): Promise<RestaurantTable> => {
    const response = await api.patch(`/tables/${id}/clear`);
    return normalizeTable(
      (response.data?.data ?? response.data ?? {}) as Record<string, unknown>
    );
  },

  createTable: async (data: {
    branchId?: string;
    salesPointId?: string | null;
    name: string;
    capacity?: number;
    section?: string | null;
  }): Promise<RestaurantTable> => {
    const response = await api.post("/tables", data);
    return normalizeTable(
      (response.data?.data ?? response.data ?? {}) as Record<string, unknown>
    );
  },

  bulkCreateTables: async (data: {
    branchId?: string;
    salesPointId?: string | null;
    prefix: string;
    startNumber: number;
    endNumber: number;
    capacity?: number;
    section?: string | null;
  }): Promise<{ created: RestaurantTable[]; skipped: string[] }> => {
    const response = await api.post("/tables/bulk", data);
    const payload = (response.data?.data ?? response.data ?? {}) as Record<
      string,
      unknown
    >;
    const createdRaw = Array.isArray(payload.created) ? payload.created : [];
    const skipped = Array.isArray(payload.skipped)
      ? payload.skipped.map(String)
      : [];
    return {
      created: createdRaw.map((row) =>
        normalizeTable(row as Record<string, unknown>)
      ),
      skipped,
    };
  },

  updateTable: async (
    id: string,
    data: {
      name?: string;
      capacity?: number;
      section?: string | null;
      salesPointId?: string | null;
      isActive?: boolean;
    }
  ): Promise<RestaurantTable> => {
    const response = await api.put(`/tables/${id}`, data);
    return normalizeTable(
      (response.data?.data ?? response.data ?? {}) as Record<string, unknown>
    );
  },

  deleteTable: async (id: string): Promise<void> => {
    await api.delete(`/tables/${id}`);
  },
};
